import { motion } from 'framer-motion';
import { HelpCircle, Search, Youtube } from 'lucide-react';
import Section from '../components/Section';
import FaqItem from '../components/FaqItem';
import Button from '../components/Button';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';

const FaqPage = () => {
  const generalFaqs = [
    {
      question: 'What is SportsFuture?',
      answer: 'SportsFuture is a decentralized platform on the Apertum blockchain designed to empower athletes through scholarships and grants. We connect promising athletes with funding opportunities, community support, and resources needed to succeed.'
    },
    {
      question: 'How does the platform work?',
      answer: 'Athletes apply through our submission form, which is reviewed by our community and AI system. Once approved, a profile is created where supporters can contribute funding. We leverage blockchain technology to ensure transparent, efficient fund distribution.'
    },
    {
      question: 'What are the eligibility criteria?',
      answer: 'SportsFuture is open to athletes of all levels, from beginners to professionals. The primary requirements are dedication to your sport, clear goals, and a compelling motivation for why you deserve support. Each application is evaluated on its individual merits.'
    }
  ];

  const applicationFaqs = [
    {
      question: 'How do I apply?',
      answer: 'To apply, navigate to the "Apply" section of our website and complete the athlete submission form. You\'ll need to provide details about your sporting achievements, goals, and why you\'re seeking support. The more detailed and honest your application, the better your chances of approval.'
    },
    {
      question: 'What documents do I need to submit?',
      answer: 'While not mandatory, we recommend uploading documents that support your application, such as training records, competition results, or letters of recommendation. You can also include a video showcasing your athletic abilities or training routine.'
    },
    {
      question: 'How long does the application review process take?',
      answer: 'The typical review process takes 1-2 weeks. Applications are evaluated by a combination of community members and our AI system to ensure fair and comprehensive assessment. You\'ll receive email notifications about your application status throughout the process.'
    }
  ];

  const tokenFaqs = [
    {
      question: 'What is the SFUT token?',
      answer: 'SFUT is the native token of the SportsFuture ecosystem. It enables participation in governance decisions, staking for rewards, direct funding of athletes, and access to premium features. The token is built on the Apertum blockchain to ensure security and efficiency.'
    },
    {
      question: 'How can I acquire SFUT tokens?',
      answer: 'SFUT tokens are available on the Apertum decentralized exchange. You can also earn tokens through various community activities, such as participating in governance, referring athletes, or contributing to development initiatives.'
    },
    {
      question: 'What are the benefits of holding SFUT tokens?',
      answer: 'SFUT token holders can participate in governance voting, earn staking rewards, gain access to exclusive athlete events, and receive early access to new platform features. The value of the token is tied to the growth and success of the SportsFuture ecosystem.'
    }
  ];

  const supportFaqs = [
    {
      question: 'How can I support athletes?',
      answer: 'You can support athletes by contributing SFUT tokens directly to their profiles, participating in community discussions, offering mentorship, or providing other resources. Every contribution, regardless of size, helps athletes pursue their dreams.'
    },
    {
      question: 'What happens to my contribution?',
      answer: 'Your contributions go directly to the athlete\'s funding pool, with a small percentage allocated to platform maintenance. Funds are released to athletes based on predetermined milestones to ensure accountability and proper use of resources.'
    },
    {
      question: 'Can I track how my contribution is used?',
      answer: 'Yes, the blockchain ensures full transparency of all transactions. You can track how funds are distributed and utilized through our platform. Athletes also provide regular updates on their progress and how the funding has supported their development.'
    }
  ];

  return (
    <>
      <PageHero
        eyebrow="Support"
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about SportsFuture, how it works, and how you can get involved."
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="mb-8">
          <div className="flex items-center border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm sf-surface-card sf-ui-system">
            <div className="px-4">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search for questions..."
              className="w-full py-3 px-2 focus:outline-none"
            />
          </div>
        </div>
        
        <div className="space-y-12">
          <div>
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <HelpCircle className="mr-2 text-primary" />
              General Questions
            </h2>
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              {generalFaqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <HelpCircle className="mr-2 text-primary" />
              Application Process
            </h2>
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              {applicationFaqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <HelpCircle className="mr-2 text-primary" />
              Token Information
            </h2>
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              {tokenFaqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <HelpCircle className="mr-2 text-primary" />
              Supporting Athletes
            </h2>
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              {supportFaqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-16 bg-gray-100 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-gray-700 mb-6 max-w-xl mx-auto">
            If you couldn't find the answer to your question, please feel free to reach out to our team directly.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button to="/contact" variant="primary">
              Contact Us
            </Button>
            <Button
              as="a"
              href="https://www.youtube.com/playlist?list=PLHlf_ssJTHkfQBydGLUswFIUIRuTi7Jov"
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              className="inline-flex items-center"
            >
              <Youtube className="w-5 h-5 mr-2" />
              Watch FAQ Videos
            </Button>
          </div>
        </div>
      </motion.div>
      </Section>
    </>
  );
};

export default FaqPage;